import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { fichar, getFichajes, BACKEND_URL } from "../../api/api";
import { getGruposTrabajo } from "../../api/gruposTrabajo";
import { getJornadas } from "../../api/jornadas";
import { useNavigate } from "react-router-dom";
import CalendarioFichajes from "../../components/calendario/CalendarioFichajes";
import SolicitudesTrabajador from "../../components/trabajador/SolicitudesTrabajador";
import FichajesOfflineStatus from "../../components/trabajador/FichajesOfflineStatus";

/**
 * Panel principal para trabajadores.
 * Permite fichar entradas/salidas, ver resumen de tiempos trabajados, calendario de fichajes
 * y gestionar solicitudes de ausencias/vacaciones.
 * Incluye validaciones de jornadas y confirmaciones de fichaje.
 * 
 * @returns {JSX.Element} Dashboard completo del trabajador
 */
export default function DashboardTrabajador() {
  const { token, user, logout, reloadUser } = useContext(AuthContext);
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [mensajeExito, setMensajeExito] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSolicitudes, setShowSolicitudes] = useState(false);
  const [solicitudesViewMode, setSolicitudesViewMode] = useState("both"); // 'form' | 'list' | 'both'

  const handleLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const [fichajes, setFichajes] = useState([]);
  const [fechaFiltro, setFechaFiltro] = useState(() => {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tiempoTrabajadoMin, setTiempoTrabajadoMin] = useState(0);
  const [tiempoRestanteMin, setTiempoRestanteMin] = useState(0);

  const [puedeFicharEntrada, setPuedeFicharEntrada] = useState(true);
  const [puedeFicharSalida, setPuedeFicharSalida] = useState(false);

  // Estados para confirmación
  const [confirmarEntrada, setConfirmarEntrada] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const [jornadaRegistrada, setJornadaRegistrada] = useState(false);

  // Referencias para los timeouts
  const timeoutEntrada = useRef(null);
  const timeoutSalida = useRef(null);
  const syncingRef = useRef(false);

  const getPendientesCount = () => {
    try {
      const str = localStorage.getItem('fichajes-pendientes');
      const todos = str ? JSON.parse(str) : [];
      return todos.filter(f => f.token === token).length;
    } catch { return 0; }
  };

  const cargarFichajes = async (fechaISO) => {
    try {
      setError("");
      setLoading(true);
      setFichajes([]);

      if (!token) {
        throw new Error("No autorizado");
      }
      
      if (!user || !user._id) {
        throw new Error("Usuario no disponible");
      }

      const d = new Date(fechaISO);
      if (isNaN(d.getTime())) throw new Error("Fecha inválida");

      const anio = d.getFullYear();
      const mes = d.getMonth() + 1;
      const dia = d.getDate();

      const query = `?anio=${anio}&mes=${mes}&dia=${dia}`;
      
      const data = await getFichajes(token, query);
      
      // Convertir a array si es un objeto pseudo-array (con propiedades 0, 1, 2, etc)
      let dataArray;
      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data && typeof data === 'object') {
        // Filtrar solo propiedades numéricas (ignorar _cached, _cacheTime, etc)
        const keys = Object.keys(data).filter(k => !isNaN(parseInt(k)));
        dataArray = keys.map(k => data[k]);
      } else {
        dataArray = [];
      }

      // Solo los fichajes de este usuario (comparar como strings por seguridad)
      const misFichajes = dataArray.filter(
        (f) => String(f.userId?._id) === String(user?._id)
      );
      
      // IMPORTANTE: Añadir fichajes pendientes de localStorage para mantener estado correcto
      const pendientesStr = localStorage.getItem('fichajes-pendientes');
      if (pendientesStr) {
        try {
          const todosPendientes = JSON.parse(pendientesStr);
          const misPendientes = todosPendientes.filter(f => f.token === token);
          
          // Convertir fichajes pendientes al formato correcto y filtrar por fecha actual
          const fichajesPendientesHoy = misPendientes
            .filter(fp => {
              const fpDate = new Date(fp.timestamp);
              const today = new Date(fechaISO);
              return fpDate.getFullYear() === today.getFullYear() &&
                     fpDate.getMonth() === today.getMonth() &&
                     fpDate.getDate() === today.getDate();
            })
            .map(fp => ({
              tipo: fp.tipo,
              fecha: fp.timestamp,
              userId: { _id: user._id },
              _id: fp.uuid,
              offline: true // Marcador para identificar fichajes offline
            }));
          
          // Combinar fichajes del backend con los pendientes
          misFichajes.push(...fichajesPendientesHoy);
        } catch (error) {
          console.error('Error al procesar fichajes pendientes:', error);
        }
      }
      
      setFichajes(misFichajes);

      // Filtrar fichajes SOLO del día actual para cálculos
      const hoyParaComparar = new Date(fechaISO);
      const fichajesDelDia = misFichajes.filter(f => {
        const fechaFichaje = new Date(f.fecha);
        return fechaFichaje.getFullYear() === hoyParaComparar.getFullYear() &&
               fechaFichaje.getMonth() === hoyParaComparar.getMonth() &&
               fechaFichaje.getDate() === hoyParaComparar.getDate();
      });

      // Ordenar fichajes cronológicamente y calcular entradas abiertas (sin salida)
      const fichajesOrdenados = [...fichajesDelDia].sort(
        (a, b) => new Date(a.fecha) - new Date(b.fecha)
      );
      let openEntradasCount = 0;
      let entradasHoy = 0;
      let salidasHoy = 0;
      for (const f of fichajesOrdenados) {
        if (f.tipo === "entrada") {
          entradasHoy += 1;
          openEntradasCount += 1;
        } else if (f.tipo === "salida") {
          salidasHoy += 1;
          if (openEntradasCount > 0) openEntradasCount -= 1;
        }
      }
      // Mientras haya una entrada abierta, no permitir nueva entrada
      setPuedeFicharSalida(openEntradasCount > 0);

      // Obtener jornadas efectivas del usuario (ya vienen calculadas desde el backend)
      // jornadasEffective = jornadas directas del usuario + jornadas heredadas de grupos (sin duplicados)
      let jornadasForCalc = user?.jornadasEffective || [];

      // Si no hay jornadasEffective, usar jornadas directas como fallback
      if (!jornadasForCalc || jornadasForCalc.length === 0) {
        jornadasForCalc = user?.jornadas || [];
      }

      // Si las jornadas no contienen el campo horario (solo ids), obtener datos completos
      const jornadasNeedFetch = jornadasForCalc.some((j) => !j || !j.horario);
      if (jornadasNeedFetch) {
        try {
          const all = await getJornadas(token);
          const mapAll = new Map(all.map((x) => [String(x._id), x]));
          jornadasForCalc = jornadasForCalc
            .map((j) => {
              const id = String(j._id || j);
              return mapAll.get(id) || j;
            })
            .filter(Boolean);
        } catch (e) {
        }
      }

      const jornadasCount = jornadasForCalc.length;

      // LÓGICA DE FICHAJE:
      // - El usuario puede fichar tantas veces como jornadas tenga (un par entrada/salida por jornada)
      // - No puede fichar entrada si ya tiene una entrada abierta (sin salida)
      // - Puede fichar salida solo si tiene una entrada abierta
      // - Cuando completa todos los pares entrada/salida, no puede fichar más ese día

      const maxFichajes = jornadasCount; // Máximo de pares entrada/salida permitidos
      let entradas = 0,
        salidas = 0;
      fichajesOrdenados.forEach((f) => {
        if (f.tipo === "entrada") entradas++;
        if (f.tipo === "salida") salidas++;
      });

      const entradasAbiertas = entradas - salidas; // Entradas sin salida
      const paresCompletados = salidas; // Pares entrada/salida completados

      // Puede fichar entrada si: NO hay entrada abierta Y no ha alcanzado el máximo de pares
      const puedeEntrada =
        entradasAbiertas === 0 && paresCompletados < maxFichajes;
      setPuedeFicharEntrada(puedeEntrada);

      // Puede fichar salida si: hay una entrada abierta
      const puedeSalida = entradasAbiertas > 0;
      setPuedeFicharSalida(puedeSalida);

      // Jornada completa si: tiene jornadas Y ha completado todos los pares Y no hay entradas abiertas
      const jornadaCompleta =
        jornadasCount > 0 &&
        paresCompletados >= maxFichajes &&
        entradasAbiertas === 0;
      setJornadaRegistrada(jornadaCompleta);

      // CALCULAR TIEMPO TRABAJADO:
      // Suma todos los pares entrada/salida completados + tiempo desde entrada abierta hasta ahora
      let trabajadoMs = 0;
      let ultimaEntrada = null;

      fichajesOrdenados.forEach((f) => {
        if (f.tipo === "entrada") {
          ultimaEntrada = new Date(f.fecha).getTime();
        } else if (f.tipo === "salida" && ultimaEntrada !== null) {
          const salidaTs = new Date(f.fecha).getTime();
          if (salidaTs > ultimaEntrada) {
            trabajadoMs += salidaTs - ultimaEntrada;
          }
          ultimaEntrada = null; // Cerrar el par
        }
      });

      // Si hay una entrada abierta (sin salida), sumar tiempo hasta ahora
      if (ultimaEntrada !== null) {
        trabajadoMs += Date.now() - ultimaEntrada;
      }

      const trabajadoMin = Math.floor(trabajadoMs / 60000);
      setTiempoTrabajadoMin(trabajadoMin);

      // --- Calcular tiempo esperado según jornadasForCalc para el día de hoy ---
      const todayIndex = new Date().getDay(); // 0 Domingo .. 6 Sábado

      const mapNameToIndex = (s) => {
        if (s === undefined || s === null) return null;
        const key = String(s).trim().toLowerCase();
        if (/^[0-6]$/.test(key)) return parseInt(key, 10);
        if (/^[1-7]$/.test(key)) return parseInt(key, 10) % 7;
        const map = {
          domingo: 0,
          dom: 0,
          sun: 0,
          lunes: 1,
          lun: 1,
          mon: 1,
          martes: 2,
          mar: 2,
          tue: 2,
          miércoles: 3,
          miercoles: 3,
          mié: 3,
          mie: 3,
          wed: 3,
          jueves: 4,
          jue: 4,
          thu: 4,
          viernes: 5,
          vie: 5,
          fri: 5,
          sábado: 6,
          sabado: 6,
          sáb: 6,
          sab: 6,
          sat: 6,
        };
        return map[key] ?? null;
      };

      const isDiaMatch = (diaField, todayIdx) => {
        if (diaField === undefined || diaField === null) return false;
        const raw = String(diaField).toLowerCase();
        // permitir separadores comunes y normalizar
        // ejemplos: "Lunes a Viernes", "Lunes-Viernes", "Lunes, Miercoles"
        // dividir por comas primero
        const parts = raw
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean);
        for (const part of parts) {
          // normalizar conectores a '-'
          const norm = part.replace(/\s+a\s+|\s+to\s+|\s*-\s*/g, "-").trim();
          if (norm.includes("-")) {
            const [startRaw, endRaw] = norm
              .split("-")
              .map((x) => x.trim())
              .filter(Boolean);
            const start = mapNameToIndex(startRaw);
            const end = mapNameToIndex(endRaw);
            if (start === null || end === null) continue;
            if (start <= end) {
              if (todayIdx >= start && todayIdx <= end) return true;
            } else {
              // rango que envuelve el fin de semana, ej: Vie - Lun
              if (todayIdx >= start || todayIdx <= end) return true;
            }
          } else {
            const idx = mapNameToIndex(norm);
            if (idx !== null && idx === todayIdx) return true;
          }
        }
        return false;
      };

      let esperadoMin = 0;
      if (jornadasForCalc && Array.isArray(jornadasForCalc)) {
        jornadasForCalc.forEach((j) => {
          if (!j || !j.horario) return;
          j.horario.forEach((h) => {
            if (!h) return;
            const match = isDiaMatch(h.dia, todayIndex);
            if (!match) return;
            const [hiH, hiM] = (h.horaInicio || "00:00").split(":").map(Number);
            const [hfH, hfM] = (h.horaFin || "00:00").split(":").map(Number);
            const minutosSegmento = hfH * 60 + hfM - (hiH * 60 + hiM);
            if (!isNaN(minutosSegmento) && minutosSegmento > 0)
              esperadoMin += minutosSegmento;
          });
        });
      }
      setTiempoRestanteMin(Math.max(0, esperadoMin - trabajadoMin));
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  // Al montar, recargar datos del usuario y luego cargar fichajes

  // Al montar, limpiar estados y recargar usuario y fichajes (sin depender de caché)
  useEffect(() => {
    setFichajes([]);
    setMensajeExito("");
    setMensajeError("");
    setJornadaRegistrada(false);
    setPuedeFicharEntrada(true);
    setPuedeFicharSalida(false);
    
    (async () => {
      // Asegurar que el usuario esté cargado
      if (reloadUser) {
        await reloadUser();
      }
      
      // Esperar un tick para asegurar que user está disponible
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (user && user._id) {
        await cargarFichajes(fechaFiltro);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modalOpen) cargarFichajes(fechaFiltro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen) cargarFichajes(fechaFiltro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaFiltro]);

  // Resetear estado de jornada cuando cambia la fecha
  useEffect(() => {
    setJornadaRegistrada(false);
    setPuedeFicharEntrada(true);
    setPuedeFicharSalida(false);
  }, [fechaFiltro]);
  
  // Cargar fichajes cuando el usuario esté disponible
  useEffect(() => {
    if (user && user._id && token) {
      cargarFichajes(fechaFiltro);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  // SINCRONIZACIÓN AUTOMÁTICA DE FICHAJES OFFLINE
  const sincronizarFichajesPendientes = async () => {
    if (!token || !navigator.onLine) return;
    if (syncingRef.current) return;
    syncingRef.current = true;

    try {
      const pendientesStr = localStorage.getItem('fichajes-pendientes');
      if (!pendientesStr) { syncingRef.current = false; return; }

      const todosPendientes = JSON.parse(pendientesStr);
      const misPendientes = todosPendientes.filter(f => f.token === token);
      const otrosPendientes = todosPendientes.filter(f => f.token !== token);

      if (misPendientes.length === 0) { syncingRef.current = false; return; }


      let sincronizados = 0;
      let errores = 0;
      const pendientesRestantes = [...otrosPendientes];

      for (const fp of misPendientes) {
        try {
          // ⚠️ CRÍTICO: pasar timestamp original + offline + uuid para que el backend
          // registre la hora en que se pulsó el botón, NO la hora de sincronización
          await fichar(token, fp.tipo, {
            timestamp: fp.timestamp,
            clientTimestamp: fp.timestamp,
            offline: true,
            uuid: fp.uuid,
          });
          sincronizados++;
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'sin detalle';
          // Si ya existe (uuid duplicado) lo descartamos igualmente
          if (err?.response?.status === 409 || msg.includes('ya existe') || msg.includes('duplicado')) {
            // descartado silenciosamente
          } else {
            pendientesRestantes.push(fp); // conservar para el próximo intento
            errores++;
          }
        }
      }

      localStorage.setItem('fichajes-pendientes', JSON.stringify(pendientesRestantes));

      if (sincronizados > 0) {
        setMensajeExito(`✅ ${sincronizados} fichaje(s) sincronizado(s) correctamente`);
        setTimeout(() => setMensajeExito(""), 5000);
        await cargarFichajes(fechaFiltro);
      }

      if (errores > 0) {
        setMensajeError(`⚠️ ${errores} fichaje(s) no se pudieron sincronizar`);
        setTimeout(() => setMensajeError(""), 5000);
      }
    } catch (err) {
      // sync error silencioso
    } finally {
      syncingRef.current = false;
    }
  };

  // Escuchar evento de conexión + sincronizar al montar + múltiples disparadores
  useEffect(() => {
    const handleOnline = () => {
      setTimeout(() => sincronizarFichajesPendientes(), 1500);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        sincronizarFichajesPendientes();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);

    // Escuchar cambios de conexión en móvil (si disponible)
    if (navigator.connection) {
      navigator.connection.addEventListener('change', () => {
        if (navigator.onLine) setTimeout(() => sincronizarFichajesPendientes(), 2000);
      });
    }

    // Sincronizar al cargar si hay conexión
    if (navigator.onLine && token) {
      setTimeout(() => sincronizarFichajesPendientes(), 2000);
    }

    // Reintento periódico cada 30s
    const interval = setInterval(() => {
      if (navigator.onLine) sincronizarFichajesPendientes();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleFichar = async (tipo) => {
    // Cancelar los timeouts pendientes
    if (tipo === "entrada" && timeoutEntrada.current) {
      clearTimeout(timeoutEntrada.current);
      timeoutEntrada.current = null;
    } else if (tipo === "salida" && timeoutSalida.current) {
      clearTimeout(timeoutSalida.current);
      timeoutSalida.current = null;
    }

    // Resetear confirmaciones
    setConfirmarEntrada(false);
    setConfirmarSalida(false);

    // Verificar conexión ANTES de llamar a la API
    if (!navigator.onLine) {
      try {
        const clientTimestamp = new Date().toISOString();
        const fichajeOffline = {
          tipo,
          timestamp: clientTimestamp,
          token,
          uuid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: Date.now()
        };

        const pendientesStr = localStorage.getItem('fichajes-pendientes');
        const pendientes = pendientesStr ? JSON.parse(pendientesStr) : [];
        pendientes.push(fichajeOffline);
        localStorage.setItem('fichajes-pendientes', JSON.stringify(pendientes));

        const fichajeLocal = {
          tipo,
          fecha: fichajeOffline.timestamp,
          userId: { _id: user._id },
          _id: fichajeOffline.uuid
        };
        setFichajes(prev => [...prev, fichajeLocal]);

        if (tipo === 'entrada') {
          setPuedeFicharEntrada(false);
          setPuedeFicharSalida(true);
        } else {
          setPuedeFicharSalida(false);
        }

        setMensajeExito(`📴 SIN CONEXIÓN: Fichaje de ${tipo} guardado offline. Se sincronizará automáticamente.`);
        setMensajeError("");
        setTimeout(() => setMensajeExito(""), 6000);
        return;
      } catch (offlineError) {
        setMensajeError(`Error al guardar offline: ${offlineError.message}`);
        setTimeout(() => setMensajeError(""), 5000);
        return;
      }
    }

    // Fichaje normal con conexión
    try {
      const clientTimestamp = new Date().toISOString();
      const res = await fichar(token, tipo, { clientTimestamp, timeout: 6000 });
      
      setMensajeExito(
        res.mensaje || `Fichaje de ${tipo} registrado correctamente`
      );
      setMensajeError("");
      setTimeout(() => setMensajeExito(""), 4000);
      await cargarFichajes(fechaFiltro);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.message || "";
      const esErrorRed = !err.response ||
        err.code === 'ECONNABORTED' ||
        err.code === 'ERR_NETWORK' ||
        err.code === 'ERR_CANCELED' ||
        [0, 503, 504].includes(err.response?.status) ||
        ['network', 'timeout', 'fetch', 'err_network', 'err_internet', 'err_canceled', 'aborted', 'failed to fetch']
          .some(k => serverMsg.toLowerCase().includes(k));

      if (esErrorRed) {
        try {
          const fichajeOffline = {
            tipo,
            timestamp: clientTimestamp,
            token,
            uuid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            createdAt: Date.now()
          };
          const str = localStorage.getItem('fichajes-pendientes');
          const pendientes = str ? JSON.parse(str) : [];
          pendientes.push(fichajeOffline);
          localStorage.setItem('fichajes-pendientes', JSON.stringify(pendientes));

          setFichajes(prev => [...prev, {
            tipo, fecha: clientTimestamp,
            userId: { _id: user._id }, _id: fichajeOffline.uuid
          }]);
          if (tipo === 'entrada') { setPuedeFicharEntrada(false); setPuedeFicharSalida(true); }
          else { setPuedeFicharSalida(false); }

          setMensajeExito(`📴 Sin conexión: fichaje guardado offline. Se sincronizará al reconectar.`);
          setMensajeError("");
          setTimeout(() => setMensajeExito(""), 6000);
          return;
        } catch {
          // no se pudo guardar offline
        }
      }

      setConfirmarEntrada(false);
      setConfirmarSalida(false);

      if (
        serverMsg.toString().toLowerCase().includes("maximo") ||
        serverMsg.toString().toLowerCase().includes("máximo") ||
        serverMsg.toString().toLowerCase().includes("alcanzado")
      ) {
        setMensajeError(`No puedes fichar más: ${serverMsg}`);
        setMensajeExito("");
      } else if (serverMsg.toString().toLowerCase().includes("no hay entradas sin emparejar")) {
        setMensajeError("No hay entrada registrada para poder fichar salida");
        setMensajeExito("");
      } else if (serverMsg.toString().toLowerCase().includes("jornadas asignadas")) {
        setMensajeError("No tienes jornadas asignadas para hoy");
        setMensajeExito("");
      } else {
        setMensajeError(`Error: ${serverMsg || "Inténtalo de nuevo"}`);
        setMensajeExito("");
      }

      setTimeout(() => setMensajeError(""), 5000);
      await cargarFichajes(fechaFiltro);
    }
  };

  const handleClickFichar = (tipo) => {
    if (tipo === "entrada") {
      if (confirmarEntrada) {
        handleFichar(tipo);
      } else {
        setConfirmarEntrada(true);
        timeoutEntrada.current = setTimeout(() => {
          setConfirmarEntrada(false);
          setMensajeError(
            "No se realizó el fichaje de entrada. Debes pulsar dos veces para confirmar."
          );
          setTimeout(() => setMensajeError(""), 4000);
        }, 3000);
      }
    } else if (tipo === "salida") {
      if (confirmarSalida) {
        handleFichar(tipo);
      } else {
        setConfirmarSalida(true);
        timeoutSalida.current = setTimeout(() => {
          setConfirmarSalida(false);
          setMensajeError(
            "No se realizó el fichaje de salida. Debes pulsar dos veces para confirmar."
          );
          setTimeout(() => setMensajeError(""), 4000);
        }, 3000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto w-full">
        {/* Botón de sincronización manual */}
        {localStorage.getItem('fichajes-pendientes') && JSON.parse(localStorage.getItem('fichajes-pendientes') || '[]').filter(f => f.token === token).length > 0 && (
          <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  📴 Tienes {JSON.parse(localStorage.getItem('fichajes-pendientes') || '[]').filter(f => f.token === token).length} fichaje(s) pendiente(s) de sincronizar
                </p>
              </div>
              <button
                onClick={sincronizarFichajesPendientes}
                disabled={!navigator.onLine}
                className="ml-4 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
              >
                {navigator.onLine ? '🔄 Sincronizar ahora' : '📴 Sin conexión'}
              </button>
            </div>
          </div>
        )}

        {/* Fichajes offline pendientes de validación o rechazados */}
        <FichajesOfflineStatus />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Panel de Fichaje */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Header - Nombre empleado, empresa y logo */}
            <div className="p-3 sm:p-4 md:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
                    {user?.nombre}
                  </h2>
                  <p className="text-gray-500 mt-1 text-xs sm:text-sm">
                    {user?.empresa?.nombre}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Botón modo oscuro */}
                  <button
                    onClick={toggleDarkMode}
                    title={darkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-600"
                  >
                    {darkMode ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                      </svg>
                    )}
                  </button>
                {user?.empresa?.imagenUrl && (
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gray-100 rounded flex items-center justify-center p-2">
                    <img
                      src={`${BACKEND_URL}${user.empresa.imagenUrl}`}
                      alt={user.empresa.nombre}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Línea separadora */}
            <div className="border-t border-gray-200"></div>

            {/* Contenido - Imagen del empleado y botones */}
            <div className="p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-center">
                {/* Imagen del empleado */}
                <div className="flex-shrink-0">
                  {user?.imagenPerfil || user?.imagenUrl ? (
                    <img
                      src={`${BACKEND_URL}${
                        user.imagenPerfil || user.imagenUrl
                      }`}
                      alt={user.nombre}
                      className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                      <i className="fas fa-user text-gray-400 text-3xl sm:text-4xl md:text-5xl"></i>
                    </div>
                  )}
                </div>

                {/* Botones de fichaje */}
                {!jornadaRegistrada ? (
                  <div className="flex-1 space-y-3 w-full">
                    {/* Mensaje de no jornada asignada */}
                    {(!user?.jornadasEffective || user.jornadasEffective.length === 0) && 
                     (!user?.jornadas || user.jornadas.length === 0) ? (
                      <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-3 py-2 sm:px-4 sm:py-3 rounded-lg">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <i className="fas fa-exclamation-triangle text-base sm:text-xl mt-0.5"></i>
                          <div>
                            <p className="font-semibold mb-1 text-sm sm:text-base">No tienes jornada asignada</p>
                            <p className="text-xs sm:text-sm">
                              Por favor, contacta con el administrador de tu empresa para que te asigne una jornada laboral.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Entrada */}
                        <button
                          onClick={() => handleClickFichar("entrada")}
                          disabled={!puedeFicharEntrada}
                          className={`w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                            puedeFicharEntrada
                              ? confirmarEntrada
                                ? "bg-green-600 text-white animate-pulse"
                                : "bg-green-500 text-white hover:bg-green-600"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          <i className="fas fa-sign-in-alt mr-2"></i>
                          {confirmarEntrada
                            ? "Confirmar Entrada"
                            : "Fichar Entrada"}
                        </button>

                        {/* Salida */}
                        <button
                          onClick={() => handleClickFichar("salida")}
                          disabled={!puedeFicharSalida}
                          className={`w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                            puedeFicharSalida
                              ? confirmarSalida
                                ? "bg-red-600 text-white animate-pulse"
                                : "bg-red-500 text-white hover:bg-red-600"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          <i className="fas fa-sign-out-alt mr-2"></i>
                          {confirmarSalida ? "Confirmar Salida" : "Fichar Salida"}
                        </button>
                      </>
                    )}

                    {/* Ausencia eliminado */}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="fas fa-check-circle"></i>
                      <span className="text-sm">
                        Su jornada diaria ha sido registrada correctamente
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Mensajes de éxito/error */}
              {mensajeExito && (
                <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <i className="fas fa-check-circle"></i>
                  <span className="text-sm">{mensajeExito}</span>
                </div>
              )}

              {mensajeError && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  <span className="text-sm">{mensajeError}</span>
                </div>
              )}
            </div>

            {/* Sección de jornada laboral y acciones */}
            <div className="border-t border-gray-200 p-3 sm:p-4 md:p-6 space-y-4 md:space-y-5">
              {/* Jornada Laboral */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Jornada Laboral
                </h3>
                {user?.jornadasEffective &&
                user.jornadasEffective.length > 0 ? (
                  <div className="space-y-4">
                    {user.jornadasEffective.map((j) => (
                      <div
                        key={j._id}
                        className="p-2 border border-gray-100 rounded"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-gray-700">
                            {j.nombre}
                          </div>
                          {!j.activo && (
                            <div className="text-xs text-red-500">Inactiva</div>
                          )}
                        </div>
                        {j.horario && j.horario.length > 0 ? (
                          <div className="space-y-2">
                            {j.horario.map((h, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-3 border border-gray-200 p-3 rounded"
                              >
                                <i className="fas fa-clock text-gray-400 text-sm"></i>
                                <span className="text-gray-700 font-medium">
                                  {h.dia}: {h.horaInicio} - {h.horaFin}
                                  {h.descansoMinutos
                                    ? ` (Descanso: ${h.descansoMinutos} min)`
                                    : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            Sin horario definido
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">
                    No tienes una jornada asignada.
                  </div>
                )}
              </div>

              {/* Jornada Actual y Restante */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 sm:mb-2">
                    Jornada Actual
                  </h4>
                  <div className="border border-gray-300 p-2 sm:p-3 rounded bg-white">
                    <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800">
                      {Math.floor(tiempoTrabajadoMin / 60)}h{" "}
                      {tiempoTrabajadoMin % 60}m
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5 sm:mb-2">
                    Jornada Restante
                  </h4>
                  <div className="border border-gray-300 p-2 sm:p-3 rounded bg-white">
                    <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800">
                      {Math.floor(tiempoRestanteMin / 60)}h{" "}
                      {tiempoRestanteMin % 60}m
                    </p>
                  </div>
                </div>
              </div>

              {/* Botones de solicitudes: crear nueva y ver existentes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 pt-2">
                <button
                  onClick={() => {
                    setSolicitudesViewMode("form");
                    setShowSolicitudes(true);
                  }}
                  className="border-2 border-gray-700 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded hover:bg-gray-700 hover:text-white transition font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <i className="fas fa-plus-circle text-sm"></i>
                  <span>Nueva solicitud</span>
                </button>
                <button
                  onClick={() => {
                    setSolicitudesViewMode("list");
                    setShowSolicitudes(true);
                  }}
                  className="border-2 border-gray-700 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded hover:bg-gray-700 hover:text-white transition font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <i className="fas fa-file-alt text-sm"></i>
                  <span>Ver solicitudes</span>
                </button>
              </div>

              {/* Botón Panel Admin (si aplica) */}
              {user?.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  className="w-full border border-gray-300 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded hover:bg-gray-50 transition font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <i className="fas fa-arrow-left text-sm"></i>
                  Panel Admin
                </button>
              )}

              {/* Botón Cerrar Sesión */}
              <button
                onClick={() => setShowLogoutModal(true)}
                className="w-full border border-gray-300 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded hover:bg-gray-50 transition font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <i className="fas fa-sign-out-alt text-sm"></i>
                Cerrar sesión
              </button>
            </div>
          </div>

          {/* Calendario de fichajes */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <CalendarioFichajes />
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-11/12 max-w-2xl overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <i className="fas fa-clock text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold">Mis Fichajes</h3>
              </div>
            </div>{" "}
            {/* Contenido */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-calendar text-gray-400 mr-2"></i>
                  Filtrar por fecha
                </label>
                <input
                  type="date"
                  value={fechaFiltro}
                  onChange={(e) => setFechaFiltro(e.target.value)}
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  min="1900-01-01"
                  max="2079-12-31"
                />
              </div>

              {loading && (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin text-3xl text-indigo-500 mb-2"></i>
                  <p className="text-gray-600">Cargando fichajes...</p>
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}

              {!loading && (
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                      <tr>
                        <th className="p-4 font-semibold text-gray-700 border-b-2 border-gray-200">
                          <i className="fas fa-calendar-day mr-2"></i>
                          Fecha y Hora
                        </th>
                        <th className="p-4 font-semibold text-gray-700 border-b-2 border-gray-200">
                          <i className="fas fa-list-ul mr-2"></i>
                          Tipo
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fichajes.length === 0 ? (
                        <tr>
                          <td
                            className="p-8 text-center text-gray-500"
                            colSpan="2"
                          >
                            <i className="fas fa-inbox text-4xl text-gray-300 mb-2"></i>
                            <p>No hay fichajes registrados para esta fecha.</p>
                          </td>
                        </tr>
                      ) : (
                        fichajes.map((f) => (
                          <tr
                            key={f._id}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <i className="fas fa-clock text-indigo-500"></i>
                                <span>
                                  {new Date(f.fecha).toLocaleString("es-ES", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${
                                  f.tipo === "entrada"
                                    ? "bg-green-100 text-green-700 border border-green-300"
                                    : f.tipo === "salida"
                                    ? "bg-red-100 text-red-700 border border-red-300"
                                    : "bg-gray-100 text-gray-700 border border-gray-300"
                                }`}
                              >
                                <i
                                  className={`fas ${
                                    f.tipo === "entrada"
                                      ? "fa-sign-in-alt"
                                      : f.tipo === "salida"
                                      ? "fa-sign-out-alt"
                                      : "fa-ban"
                                  }`}
                                ></i>
                                {f.tipo.charAt(0).toUpperCase() +
                                  f.tipo.slice(1)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setError("");
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition font-semibold flex items-center justify-center gap-2"
                >
                  <i className="fas fa-times"></i>
                  Cerrar
                </button>
                <button
                  onClick={() => cargarFichajes(fechaFiltro)}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition font-semibold shadow-md flex items-center justify-center gap-2"
                >
                  <i className="fas fa-sync-alt"></i>
                  Refrescar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de logout */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center gap-2 md:gap-3 mb-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <i className="fas fa-sign-out-alt text-yellow-600 text-lg md:text-xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800">
                Cerrar sesión
              </h3>
            </div>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
              ¿Estás seguro de que quieres salir? Tu sesión actual se cerrará.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="bg-gray-100 text-gray-700 px-4 md:px-5 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm md:text-base"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 md:px-5 py-2.5 rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <i className="fas fa-sign-out-alt"></i>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Solicitudes */}
      {showSolicitudes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-xl font-bold text-gray-800">
                Mis Solicitudes
              </h3>
              <button
                onClick={() => {
                  setShowSolicitudes(false);
                  setSolicitudesViewMode("both");
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-2 sm:p-4">
              <SolicitudesTrabajador
                viewMode={solicitudesViewMode}
                onClose={() => {
                  setShowSolicitudes(false);
                  setSolicitudesViewMode("both");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
